/**
 * Monthly credit plans. Single source of truth for the Dodo checkout config on
 * the server, the landing-page pricing cards, and the credits page.
 * `credits` must stay in sync with the Dodo product each slug maps to — see
 * `lib/payments.ts` and the DODO_PAYMENTS_PRODUCT_ID_* env vars.
 * Client-safe: no env or server imports.
 */
export const CREDIT_PLANS = [
  {
    slug: "starter",
    name: "Starter",
    priceUsd: 5,
    futurePriceUsd: 10,
    credits: 100,
    featured: false,
    meta: "100 credits / month",
    body: "Essential AI tailoring for a focused, targeted job hunt.",
    cta: "Start with Starter",
    features: [
      "100 credits / month (1 credit = 1 rewrite or boost)",
      "All 12+ ATS-ready templates and PDF exports",
      "Verified jobs directory, search and filters",
      "Application tracker and stage analytics",
      "3 free trial credits on signup",
    ],
  },
  {
    slug: "pro",
    name: "Pro",
    priceUsd: 10,
    futurePriceUsd: 20,
    credits: 250,
    featured: true,
    meta: "250 credits + MCP / API",
    body: "For active seekers who want IDE agent and MCP automation.",
    cta: "Get Pro access",
    features: [
      "250 credits / month for rewrites and boosts",
      "MCP and REST API access to every feature",
      "Full job board API, query and matching endpoints",
      "Claude Desktop, Cursor and Windsurf integration",
      "All 12+ ATS-ready templates and PDF exports",
      "Priority inference speed and Defend Cards",
    ],
  },
  {
    slug: "ultimate",
    name: "Ultimate",
    priceUsd: 20,
    futurePriceUsd: 50,
    credits: 1000,
    featured: false,
    meta: "1000 credits / month",
    body: "High-volume applications with room to tailor every single one.",
    cta: "Get Ultimate",
    features: [
      "1000 credits / month for rewrites and boosts",
      "Everything in Pro",
      "MCP and REST API access to every feature",
      "Full job board API access and batch matching",
      "Claude Desktop and Cursor agent workflows",
      "Unlimited PDF exports across every template",
      "Highest priority processing and VIP support",
    ],
  },
] as const;

export type CreditPlanSlug = (typeof CREDIT_PLANS)[number]["slug"];
export type CreditPlan = (typeof CREDIT_PLANS)[number];

/**
 * One-time credit packs for users who exhaust a month's credits early. Bought
 * on top of a plan, or standalone. No renewal, no plan change: Dodo grants the
 * credits once at purchase and the same `credit.added` webhook mirrors them
 * into our ledger.
 *
 * These credits do not expire. Plan credits are a monthly allowance and lapse
 * on the Dodo entitlement's schedule; a pack is paid for outright, so expiring
 * it would be taking back something the user owns. That difference is why every
 * pack is priced above the per-credit rate of every plan — a permanent credit
 * must cost more than a renting one, or the top tier stops making sense.
 *
 * Requires the packs to sit on a separate Dodo credit entitlement with no
 * expiry policy. Expiry is configured per entitlement, not per product.
 */
export const TOPUP_PACKS = [
  {
    slug: "topup-100",
    name: "100 credits",
    priceUsd: 7,
    credits: 100,
    body: "A short burst of extra rewrites.",
  },
  {
    slug: "topup-300",
    name: "300 credits",
    priceUsd: 15,
    credits: 300,
    body: "Enough for a heavy application week.",
  },
  {
    slug: "topup-750",
    name: "750 credits",
    priceUsd: 30,
    credits: 750,
    body: "Best value. Tailor every application you send.",
  },
] as const;

export type TopupSlug = (typeof TOPUP_PACKS)[number]["slug"];
export type TopupPack = (typeof TOPUP_PACKS)[number];

/** Anything checkout accepts: a monthly plan or a one-time pack. */
export type CheckoutSlug = CreditPlanSlug | TopupSlug;
